import React, { useEffect, useState, useMemo } from 'react';
import {
    SelectInput,
    TextInput,
    required,
    useTranslate,
    useInput,
    useLocaleState,
} from 'react-admin';
import { Box } from '@mui/material';
import {
    getLocalizedAddressData,
    findAddressParts,
} from '../../data/hongKongAddressData';

interface HongKongAddressFieldsProps {
    countryValue?: string;
    cityValue?: string;
    stateValue?: string;
    validateRequired?: any[];
}

export const HongKongAddressFields: React.FC<HongKongAddressFieldsProps> = ({
    countryValue = 'Hong Kong',
    cityValue,
    stateValue,
    validateRequired = [required()]
}) => {
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const language = locale || 'en';
    const addressData = useMemo(() => getLocalizedAddressData(language), [language]);

    const cityInput = useInput({ source: 'city' });
    const stateInput = useInput({ source: 'state' });

    // selectedRegionKey is the ONLY piece of local state. districts and localities
    // are derived from it (+ stateValue + addressData) via useMemo, so there's no
    // way for a useEffect to wipe them out of sync with the region selection.
    const [selectedRegionKey, setSelectedRegionKey] = useState<string>('');

    // Derive districts from the current region — single source of truth.
    const districts = useMemo(() => {
        if (!selectedRegionKey) return [];
        const region = addressData.find(r => r.key === selectedRegionKey);
        return region?.districts ?? [];
    }, [selectedRegionKey, addressData]);

    // Derive localities from the currently-selected district (identified by stateValue).
    const localities = useMemo(() => {
        if (!stateValue) return [];
        for (const region of addressData) {
            const district = region.districts.find(d => d.key === stateValue);
            if (district) return district.localities;
        }
        return [];
    }, [stateValue, addressData]);

    // Seed selectedRegionKey from an existing stateValue (Edit mode initial load,
    // or if the current region doesn't own the current stateValue for any reason).
    useEffect(() => {
        if (!stateValue || addressData.length === 0) return;

        const currentRegion = selectedRegionKey
            ? addressData.find(r => r.key === selectedRegionKey)
            : undefined;
        const alreadyOwnsState = currentRegion?.districts.some(d => d.key === stateValue);
        if (alreadyOwnsState) return;

        // Try matching by district key first.
        for (const region of addressData) {
            if (region.districts.some(d => d.key === stateValue)) {
                setSelectedRegionKey(region.key);
                return;
            }
        }

        // Legacy fallback: match by city/state name.
        if (cityValue) {
            const { region } = findAddressParts(cityValue, stateValue);
            if (region) setSelectedRegionKey(region.key);
        }
    }, [cityValue, stateValue, addressData, selectedRegionKey]);

    const handleRegionChange = (event: any) => {
        const value = typeof event === 'string' ? event : event?.target?.value ?? event;
        setSelectedRegionKey(value || '');
        // Clear any previously-picked district and locality — their keys belong to
        // the previous region and won't exist in the new one's choices.
        stateInput.field.onChange('');
        cityInput.field.onChange('');
    };

    const handleDistrictChange = (event: any) => {
        const value = typeof event === 'object' && event?.target ? event.target.value : event;
        stateInput.field.onChange(value);
        // Clear locality — it belonged to the previous district.
        cityInput.field.onChange('');
    };

    const isHongKong = countryValue === 'Hong Kong';

    // Build choices — keep any current value not in the list so MUI doesn't warn
    // on Edit mode before addressData is resolved.
    const districtChoices = useMemo(() => {
        const choices = districts.map(d => ({ id: d.key, name: d.name }));
        if (stateValue && !choices.some(c => c.id === stateValue)) {
            choices.push({ id: stateValue, name: stateValue });
        }
        return choices;
    }, [districts, stateValue]);

    const localityChoices = useMemo(() => {
        const choices = localities.map(l => ({ id: l.key, name: l.name }));
        if (cityValue && !choices.some(c => c.id === cityValue)) {
            choices.push({ id: cityValue, name: cityValue });
        }
        return choices;
    }, [localities, cityValue]);

    return (
        <>
            <SelectInput
                source="country"
                choices={[
                    { id: 'Hong Kong', name: language === 'zh-TW' || language === 'zh' ? '香港' : 'Hong Kong' }
                ]}
                defaultValue="Hong Kong"
                disabled
                fullWidth
                sx={{
                    '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                    }
                }}
            />

            {isHongKong ? (
                <>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                            <SelectInput
                                source="_tempRegion"
                                label={translate('resources.sites.fields.region')}
                                choices={addressData.map(region => ({
                                    id: region.key,
                                    name: region.name
                                }))}
                                value={selectedRegionKey}
                                onChange={handleRegionChange}
                                fullWidth
                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                            <SelectInput
                                source="state"
                                label={translate('resources.sites.fields.state')}
                                choices={districtChoices}
                                onChange={handleDistrictChange}
                                disabled={!selectedRegionKey && !stateValue}
                                validate={validateRequired}
                                fullWidth
                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                            />
                        </Box>
                    </Box>

                    <SelectInput
                        source="city"
                        label={translate('resources.sites.fields.city')}
                        choices={localityChoices}
                        disabled={!stateValue && !cityValue}
                        validate={validateRequired}
                        fullWidth
                    />
                </>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextInput 
                        source="city" 
                        validate={validateRequired}
                        fullWidth
                    />
                    <TextInput 
                        source="state" 
                        validate={validateRequired}
                        fullWidth
                    />
                </Box>
            )}
        </>
    );
};